import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import {
  CustomReducedDto,
  GetAllCompanyDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { Incoterm } from "@/components/shared/incoterm";
import { InfoAccordionRow, InfoCard, InfoRow, InfoRowNested } from "./infoCard";
import { formatDate, getVolumeLabel } from "./expeditionDetail.utils";
import { ExpeditionStatusRow } from "./expeditionStatusRow";
import { PickupLocationAccordion } from "./pickupLocationAccordion";
import { PackagingAccordion } from "./packagingAccordion";
import { RecipientAccordion } from "./recipientAccordion";
import { UserAccordion } from "./userAccordion";
import { BillingEntityAccordion } from "./billingEntityAccordion";
import { BillingAccordion } from "./billingAccordion";

type ExpeditionDetailsSectionProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company?: GetAllCompanyDto;
  customs: CustomReducedDto[];
  isDeleted: boolean;
};

export const ExpeditionDetailsSection = ({
  expedition,
  company,
  customs,
  isDeleted,
}: ExpeditionDetailsSectionProps) => {
  const { t } = useTranslation(["boExpeditions", "common"]);

  const validatedAt = formatDate(expedition.validatedAt);
  const billingReference = expedition.expeditionBillingDetail?.reference;
  const finalizedByInitials = expedition.finalizedBy
    ? `${expedition.finalizedBy.firstName?.[0] ?? ""}${expedition.finalizedBy.lastName?.[0] ?? ""}`.toUpperCase()
    : null;
  const finalizedAt = expedition.finalizedAt
    ? `${formatDate(expedition.finalizedAt, "dd/MM/yyyy à HH:mm")}${finalizedByInitials ? ` (${finalizedByInitials})` : ""}`
    : null;
  const customId = expedition.quotation?.customId ?? expedition.customQuotation?.customId;
  const custom = customs.find((c) => c.id === customId);
  const insurance = expedition.expeditionOptions?.insurance
    ? t("common:boolean.yes")
    : t("common:boolean.no");
  const deliveryFrom = formatDate(expedition.deliveryInfo?.deliveryFromDate);
  const volumeLabel = getVolumeLabel(expedition, t);
  const billingDetail = expedition.expeditionBillingDetail;
  const returnBillingDetail = expedition.expeditionReturnBillingDetail;

  return (
    <InfoCard icon={ListChecks} title={t("expeditionDrawerDetails.title")}>
      <InfoAccordionRow label={t("expeditionDrawerDetails.validatedAt")} value={validatedAt}>
        {billingReference && (
          <InfoRowNested label={t("expeditionDrawerDetails.reference")} value={billingReference} />
        )}
      </InfoAccordionRow>
      <InfoRow label={t("expeditionDrawerDetails.finalizedAt")} value={finalizedAt} />
      <ExpeditionStatusRow expedition={expedition} isDeleted={isDeleted} />
      <PickupLocationAccordion expedition={expedition} company={company} />
      <InfoRow
        label={t("expeditionDrawerDetails.country")}
        value={
          custom && (
            <div className="flex items-start justify-end gap-2">
              {custom.flagCode && (
                <img
                  src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${custom.flagCode.toUpperCase()}.svg`}
                  className="mt-0.5 h-4 w-4 shrink-0"
                  alt=""
                />
              )}
              <span className="break-words">{custom.label}</span>
            </div>
          )
        }
      />
      <InfoRow
        label="Incoterm"
        value={<Incoterm dapOption={expedition.expeditionOptions?.dapOption ?? false} />}
      />
      {volumeLabel && <InfoRow label={t("expeditionDrawerDetails.volume")} value={volumeLabel} />}
      <PackagingAccordion expedition={expedition} />
      <InfoRow label={t("expeditionDrawerDetails.insurance")} value={insurance} />
      <InfoRow label={t("expeditionDrawerDetails.deliveryFrom")} value={deliveryFrom} />
      <InfoRow
        label={t("expeditionDrawerDetails.shipper")}
        value={expedition.company?.businessName}
      />
      <RecipientAccordion expedition={expedition} />
      <UserAccordion expedition={expedition} />
      <BillingEntityAccordion expedition={expedition} />
      {billingDetail && <BillingAccordion detail={billingDetail} />}
      {returnBillingDetail && <BillingAccordion detail={returnBillingDetail} isReturn />}
    </InfoCard>
  );
};
